<template>
  <page-layout>
    <template #header>
      <page-header :title="t('physical_services')">
        <template #actions>
          <Link
            class="flex items-center space-x-2 group"
            :href="
              route('physical-services.create', {
                search: $page.props.filters.search,
              })
            "
          >
            <PlusCircleIcon
              class="text-green-500 w-8 h-8 group-hover:text-green-400"
            />
            <p class="text-gray-700">
              {{ t("new_physical_service") }}
            </p>
          </Link>
        </template>
      </page-header>
    </template>

    <template #content>
      <div class="space-y-8 max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
        <form
          method="POST"
          @submit.prevent="searchPhysicalServices"
          class="flex items-end space-x-2 mb-8 max-w-lg px-4 sm:px-0"
        >
          <ui-input
            type="text"
            inputStyling="pl-10"
            :label="
              t('search_physical_services', {
                number: totalPhysicalServices,
              })
            "
            v-model="form.query"
            class="w-full"
          >
            <template #leadingContent>
              <div
                class="
                  absolute
                  inset-y-0
                  left-0
                  pl-3
                  flex
                  items-center
                  pointer-events-none
                "
                aria-hidden="true"
              >
                <SearchIcon class="mr-3 h-5 w-5 text-gray-400" />
              </div>
            </template>
          </ui-input>
          <ui-button type="submit">{{ t("search") }}</ui-button>
        </form>
        <p
          class="mt-1 text-sm text-gray-600"
          v-show="$page.props.filters.search"
        >
          {{
            t("search_results", {
              hits: physicalServices.data.length,
              number: totalPhysicalServices,
            })
          }}
        </p>
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li
              v-for="physical_service in physicalServices.data"
              :key="physical_service.id"
            >
              <Link
                :href="
                  route('physical-services.edit', {
                    physical_service: physical_service.id,
                    search: $page.props.filters.search,
                  })
                "
                class="block hover:bg-gray-50"
              >
                <div class="px-4 py-4 sm:px-6">
                  <div class="flex items-center justify-between">
                    <p class="text-sm font-medium text-light-blue-600 truncate">
                      {{ physical_service.name }}
                    </p>
                  </div>
                  <div class="mt-2 sm:flex sm:justify-between">
                    <div class="sm:flex">
                      <p
                        class="
                          mt-2
                          flex
                          items-center
                          text-xs text-gray-500
                          sm:mt-0
                        "
                      >
                        {{ physical_service.rule }}
                      </p>
                    </div>
                    <div
                      class="
                        mt-2
                        flex
                        items-center
                        text-sm text-gray-500
                        sm:mt-0
                      "
                    >
                      {{ physical_service.formatted_price }}
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          </ul>
        </div>
        <pagination
          v-show="physicalServices.data.length"
          :data="{ search: form.query }"
          class="py-3 md:px-0"
          :pagination="physicalServices"
        />
        <teleport to="#modals">
          <physical-service-creation
            @toggleVisibility="
              creatingPhysicalService = !creatingPhysicalService
            "
            :on="creatingPhysicalService"
          />
          <physical-service-edit
            v-if="physicalService"
            @toggleVisibility="editingPhysicalService = !editingPhysicalService"
            :on="editingPhysicalService"
            :physicalService="physicalService"
            :physicalServiceData="physicalServiceData"
          />
        </teleport>
      </div>
    </template>
  </page-layout>
</template>

<script>
import { PlusCircleIcon } from "@heroicons/vue/solid";
import { SearchIcon } from "@heroicons/vue/outline";
import PageHeader from "@/Shared/Organisms/PageHeader";
import AppLayout from "@/Layouts/AppLayout";
import Pagination from "@/Shared/Molecules/Pagination";
import PageLayout from "@/Layouts/PageLayout";
import PhysicalServiceCreation from "./PhysicalServices/PhysicalServiceCreation";
import PhysicalServiceEdit from "./PhysicalServices/PhysicalServiceEdit";
import { useI18n } from "vue-i18n";

export default {
  layout: AppLayout,

  props: {
    physicalService: Object,
    physicalServices: Object,
    physicalServiceData: Object,
    filters: Object,
    totalPhysicalServices: Number,
    openCreatePhysicalService: {
      type: Boolean,
      default: false,
    },
    openEditPhysicalService: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    PageLayout,
    Pagination,
    PageHeader,
    AppLayout,
    PhysicalServiceCreation,
    PhysicalServiceEdit,
    PlusCircleIcon,
    SearchIcon,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  created() {
    if (this.openCreatePhysicalService) {
      this.creatingPhysicalService = true;
    }

    if (this.openEditPhysicalService) {
      this.editingPhysicalService = true;
    }
  },

  data() {
    return {
      form: {
        query: this.filters.search,
      },
      creatingPhysicalService: false,
      editingPhysicalService: false,
    };
  },

  methods: {
    searchPhysicalServices() {
      this.$inertia.visit(this.route("physical-services.index"), {
        data: {
          search: this.form.query,
        },
      });
    },
  },
};
</script>

<i18n>
{
  "es": {
    "physical_services": "Servicios físicos",
    "search_physical_services": "Buscar {number} servicios",
    "search": "Buscar",
    "search_results": "{hits} resultado(s) buscando {number} servicios",
    "new_physical_service": "Nuevo servicio"
  }
}
</i18n>
